import { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import { selectAnalyticsDates } from '@/modules/analytics/redux/selectors';
import { isSingleDayRange } from '@/modules/analytics/utils/formatDateRangeLabel';
import { getComparisonDates } from '@/modules/analytics/utils/getComparisonDates';

import { useAiAgentsVariables } from './useAiAgentsVariables';
import { useComparisonMode } from '../../../hooks/useComparisonMode';
import type { TimeseriesDataPoint } from '../../../hooks/useOverviewTimeseries';
import type { VoiceMetricKey } from '../voiceSessionsConfig';

interface VoiceKpiValues {
  current: number;
  previous: number;
}

export interface VoiceSessionsData {
  /**
   * Per-metric values in the same unit as the chart points and formatters:
   * `totalSessions` a count, `totalMinutes` in minutes, `avgSessionLength` in
   * seconds, `voicemailRate` a percentage. Calls and minutes include voicemail;
   * the average covers answered calls only. `avgSessionLength` is 0 when no
   * session had a computable duration; the widget uses `durationCount` to render
   * the `-` placeholder instead.
   */
  kpiValues: Record<VoiceMetricKey, VoiceKpiValues>;
  durationCount: VoiceKpiValues;
  totalDurationSeconds: VoiceKpiValues;
  voicemailSessions: VoiceKpiValues;
  voicemailMinutes: VoiceKpiValues;
  voicemailDurationSeconds: VoiceKpiValues;
  voicemailDurationCount: VoiceKpiValues;
  voicemailTimeseries: VoicemailDurationPoint[];
  timeseries: Record<VoiceMetricKey, TimeseriesDataPoint[]>;
  comparisonTimeseries: Record<VoiceMetricKey, TimeseriesDataPoint[]>;
}

export interface VoicemailDurationPoint {
  date: string;
  sessions: number;
  durationSeconds: number;
  durationCount: number;
}

interface VoiceOverviewRow {
  total_voice_sessions: number | null;
  call_duration_seconds: number | null;
  call_duration_count: number | null;
  voicemail_sessions: number | null;
  voicemail_call_duration_seconds: number | null;
  voicemail_duration_count: number | null;
}

export interface VoiceTimeseriesRow {
  time_bucket: string;
  total_voice_sessions: number | null;
  call_duration_seconds: number | null;
  call_duration_count: number | null;
  voicemail_sessions: number | null;
  voicemail_call_duration_seconds: number | null;
  voicemail_duration_count: number | null;
}

const OVERVIEW_FIELDS = `
  total_voice_sessions
  call_duration_seconds
  call_duration_count
  voicemail_sessions
  voicemail_call_duration_seconds
  voicemail_duration_count
`;

const TIMESERIES_FIELDS = `
  time_bucket
  total_voice_sessions
  call_duration_seconds
  call_duration_count
  voicemail_sessions
  voicemail_call_duration_seconds
  voicemail_duration_count
`;

// Distinct operation names so Apollo's cache treats current and comparison
// queries as separate (same shape, different time window).
const GET_AI_AGENTS_VOICE_SESSIONS_SUMMARY = gql`
  query AiAgentsVoiceSessionsSummary(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $deskIds: _uuid
    $channels: _text
    $isProactive: Boolean
    $csatBrain: _int4
    $isTest: Boolean
  ) {
    rows: ai_agents_voice_sessions_summary(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        desk_ids: $deskIds
        channels: $channels
        is_proactive: $isProactive
        csat_brain: $csatBrain
        is_test: $isTest
      }
    ) {
      ${OVERVIEW_FIELDS}
    }
  }
`;

const GET_AI_AGENTS_VOICE_SESSIONS_SUMMARY_COMPARISON = gql`
  query AiAgentsVoiceSessionsSummaryComparison(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $deskIds: _uuid
    $channels: _text
    $isProactive: Boolean
    $csatBrain: _int4
    $isTest: Boolean
  ) {
    rows: ai_agents_voice_sessions_summary(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        desk_ids: $deskIds
        channels: $channels
        is_proactive: $isProactive
        csat_brain: $csatBrain
        is_test: $isTest
      }
    ) {
      ${OVERVIEW_FIELDS}
    }
  }
`;

const GET_AI_AGENTS_VOICE_SESSIONS_TIMESERIES = gql`
  query AiAgentsVoiceSessionsTimeseries(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $deskIds: _uuid
    $channels: _text
    $isProactive: Boolean
    $csatBrain: _int4
    $isTest: Boolean
  ) {
    rows: ai_agents_voice_sessions_timeseries(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        desk_ids: $deskIds
        channels: $channels
        is_proactive: $isProactive
        csat_brain: $csatBrain
        is_test: $isTest
      }
    ) {
      ${TIMESERIES_FIELDS}
    }
  }
`;

const GET_AI_AGENTS_VOICE_SESSIONS_TIMESERIES_COMPARISON = gql`
  query AiAgentsVoiceSessionsTimeseriesComparison(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $deskIds: _uuid
    $channels: _text
    $isProactive: Boolean
    $csatBrain: _int4
    $isTest: Boolean
  ) {
    rows: ai_agents_voice_sessions_timeseries(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        desk_ids: $deskIds
        channels: $channels
        is_proactive: $isProactive
        csat_brain: $csatBrain
        is_test: $isTest
      }
    ) {
      ${TIMESERIES_FIELDS}
    }
  }
`;

const EMPTY_KPI: VoiceKpiValues = { current: 0, previous: 0 };

const EMPTY_KPI_VALUES: Record<VoiceMetricKey, VoiceKpiValues> = {
  totalSessions: EMPTY_KPI,
  totalMinutes: EMPTY_KPI,
  avgSessionLength: EMPTY_KPI,
  voicemailRate: EMPTY_KPI,
};

const emptyTimeseriesRecord = (): Record<VoiceMetricKey, TimeseriesDataPoint[]> => ({
  totalSessions: [],
  totalMinutes: [],
  avgSessionLength: [],
  voicemailRate: [],
});

/**
 * Maps raw voice timeseries rows into per-metric chart point arrays.
 *
 * Durations are carrier seconds, or message-time seconds for a session with no carrier row.
 * `voicemail_duration_count` is the only voicemail count the backend has, so a voicemail session
 * with carrier seconds but no `last_message_time` leaves the average slightly skewed (rare).
 *
 * Units are preserved as SECONDS from the backend: `totalMinutes` divides by 60,
 * `avgSessionLength` divides the duration total by its own sample count and is
 * left in seconds. Sessions and minutes include voicemail; the average excludes it.
 * A bucket with no calls adds no `voicemailRate` point, and one with no answered
 * duration adds no `avgSessionLength` point (a zero denominator).
 */
export const buildVoiceTimeseriesByMetric = (
  rows: VoiceTimeseriesRow[] | undefined
): Record<VoiceMetricKey, TimeseriesDataPoint[]> => {
  const result = emptyTimeseriesRecord();
  if (!rows?.length) {
    return result;
  }

  for (const row of rows) {
    const date = row.time_bucket;
    const totalSessions = row.total_voice_sessions ?? 0;
    const callSeconds = row.call_duration_seconds ?? 0;
    const answeredSeconds = callSeconds - (row.voicemail_call_duration_seconds ?? 0);
    const durationCount = (row.call_duration_count ?? 0) - (row.voicemail_duration_count ?? 0);

    result.totalSessions.push({ date, value: totalSessions });
    result.totalMinutes.push({ date, value: callSeconds / 60 });

    if (totalSessions > 0) {
      const voicemailRate = ((row.voicemail_sessions ?? 0) / totalSessions) * 100;
      result.voicemailRate.push({ date, value: voicemailRate });
    }
    if (durationCount > 0) {
      result.avgSessionLength.push({ date, value: answeredSeconds / durationCount });
    }
  }

  return result;
};

/** Per-bucket voicemail counts and duration totals, for the export. */
export const buildVoicemailDurationPoints = (
  rows: VoiceTimeseriesRow[] | undefined
): VoicemailDurationPoint[] =>
  (rows ?? []).map((row) => ({
    date: row.time_bucket,
    sessions: row.voicemail_sessions ?? 0,
    durationSeconds: row.voicemail_call_duration_seconds ?? 0,
    durationCount: row.voicemail_duration_count ?? 0,
  }));

export const useVoiceSessions = () => {
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);

  // Mirror the AI Agents widget: skip the timeseries query on single-day ranges
  // (the chart isn't rendered for those, so the round-trip is wasted).
  const isSingleDay = isSingleDayRange(startDate, endDate);

  const { variables: currentVariables, skip } = useAiAgentsVariables();
  const comparisonDates = useMemo(
    () => getComparisonDates(startDate, endDate),
    [startDate, endDate]
  );
  const { variables: comparisonVariables } = useAiAgentsVariables(comparisonDates);

  const {
    data: currentData,
    loading: currentLoading,
    error: currentError,
    refetch: refetchCurrent,
  } = useQuery<{ rows: VoiceOverviewRow[] }>(GET_AI_AGENTS_VOICE_SESSIONS_SUMMARY, {
    variables: currentVariables,
    skip,
  });

  const {
    data: comparisonData,
    loading: comparisonLoading,
    error: comparisonError,
    refetch: refetchComparison,
  } = useQuery<{ rows: VoiceOverviewRow[] }>(GET_AI_AGENTS_VOICE_SESSIONS_SUMMARY_COMPARISON, {
    variables: comparisonVariables,
    skip: skip || !isComparing,
  });

  const {
    data: timeseriesData,
    loading: timeseriesLoading,
    error: timeseriesError,
    refetch: refetchTimeseries,
  } = useQuery<{ rows: VoiceTimeseriesRow[] }>(GET_AI_AGENTS_VOICE_SESSIONS_TIMESERIES, {
    variables: currentVariables,
    skip: skip || isSingleDay,
  });

  const {
    data: comparisonTimeseriesData,
    loading: comparisonTimeseriesLoading,
    error: comparisonTimeseriesError,
    refetch: refetchComparisonTimeseries,
  } = useQuery<{ rows: VoiceTimeseriesRow[] }>(GET_AI_AGENTS_VOICE_SESSIONS_TIMESERIES_COMPARISON, {
    variables: comparisonVariables,
    skip: skip || !isComparing || isSingleDay,
  });

  const currentRow = currentData?.rows?.[0] ?? null;
  const comparisonRow = comparisonData?.rows?.[0] ?? null;

  const isEmpty = !currentRow || (currentRow.total_voice_sessions ?? 0) === 0;

  const timeseriesByMetric = useMemo(
    () => buildVoiceTimeseriesByMetric(timeseriesData?.rows),
    [timeseriesData?.rows]
  );

  const voicemailTimeseries = useMemo(
    () => buildVoicemailDurationPoints(timeseriesData?.rows),
    [timeseriesData?.rows]
  );

  const comparisonTimeseriesByMetric = useMemo(
    () => buildVoiceTimeseriesByMetric(comparisonTimeseriesData?.rows),
    [comparisonTimeseriesData?.rows]
  );

  const data = useMemo<VoiceSessionsData>(() => {
    // Calls and call time include voicemail; the average covers answered calls only.
    const calls = (row: VoiceOverviewRow | null): number => row?.total_voice_sessions ?? 0;
    const callSeconds = (row: VoiceOverviewRow | null): number => row?.call_duration_seconds ?? 0;
    const voicemails = (row: VoiceOverviewRow | null): number => row?.voicemail_sessions ?? 0;
    const voicemailRate = (row: VoiceOverviewRow | null): number =>
      calls(row) > 0 ? (voicemails(row) / calls(row)) * 100 : 0;
    const answeredSeconds = (row: VoiceOverviewRow | null): number =>
      callSeconds(row) - (row?.voicemail_call_duration_seconds ?? 0);
    const answeredDurationCount = (row: VoiceOverviewRow | null): number =>
      (row?.call_duration_count ?? 0) - (row?.voicemail_duration_count ?? 0);
    const avgSeconds = (row: VoiceOverviewRow | null): number => {
      const count = answeredDurationCount(row);
      return count > 0 ? answeredSeconds(row) / count : 0;
    };

    const kpiValues: Record<VoiceMetricKey, VoiceKpiValues> = {
      totalSessions: { current: calls(currentRow), previous: calls(comparisonRow) },
      totalMinutes: {
        current: callSeconds(currentRow) / 60,
        previous: callSeconds(comparisonRow) / 60,
      },
      avgSessionLength: { current: avgSeconds(currentRow), previous: avgSeconds(comparisonRow) },
      voicemailRate: { current: voicemailRate(currentRow), previous: voicemailRate(comparisonRow) },
    };

    const voicemailSeconds: VoiceKpiValues = {
      current: currentRow?.voicemail_call_duration_seconds ?? 0,
      previous: comparisonRow?.voicemail_call_duration_seconds ?? 0,
    };

    return {
      kpiValues: currentRow ? kpiValues : EMPTY_KPI_VALUES,
      durationCount: {
        current: answeredDurationCount(currentRow),
        previous: answeredDurationCount(comparisonRow),
      },
      totalDurationSeconds: {
        current: callSeconds(currentRow),
        previous: callSeconds(comparisonRow),
      },
      voicemailSessions: { current: voicemails(currentRow), previous: voicemails(comparisonRow) },
      voicemailMinutes: {
        current: voicemailSeconds.current / 60,
        previous: voicemailSeconds.previous / 60,
      },
      voicemailDurationSeconds: voicemailSeconds,
      voicemailDurationCount: {
        current: currentRow?.voicemail_duration_count ?? 0,
        previous: comparisonRow?.voicemail_duration_count ?? 0,
      },
      voicemailTimeseries,
      timeseries: timeseriesByMetric,
      comparisonTimeseries: comparisonTimeseriesByMetric,
    };
  }, [
    currentRow,
    comparisonRow,
    timeseriesByMetric,
    comparisonTimeseriesByMetric,
    voicemailTimeseries,
  ]);

  return {
    data,
    isLoading:
      currentLoading ||
      timeseriesLoading ||
      (isComparing && (comparisonLoading || comparisonTimeseriesLoading)),
    error: currentError ?? timeseriesError ?? comparisonError ?? comparisonTimeseriesError ?? null,
    isEmpty,
    // Retry only the failed queries: refetch fires a skipped query too.
    refetch: () => {
      if (currentError) {
        void refetchCurrent();
      }
      if (comparisonError) {
        void refetchComparison();
      }
      if (timeseriesError) {
        void refetchTimeseries();
      }
      if (comparisonTimeseriesError) {
        void refetchComparisonTimeseries();
      }
    },
  };
};
